<script>
export default {
  title: 'Project 3: The "Animal" Game',
  starterId: 'proj3.zip'
}
</script>

<template>
  <section>

    <p>For this assignment, you gain experience with using strings and pointers
      in C.</p>

    <article>
      <h2>Readings</h2>
      <ul>
        <li><a href="http://proquest.safaribooksonline.com/book/programming/c/1565923065/9dot-variable-scope-and-functions/pcp3-chp-9-sect-5"><cite>Practical C Programming (3rd Edition)</cite> Section 9.5</a></li>
        <li><a href="http://proquest.safaribooksonline.com/book/programming/c/1565923065/iidot-simple-programming/pcp3-chp-23-sect-5"><cite>Practical C Programming (3rd Edition)</cite> Chapter 10</a></li>
        <li><a href="http://proquest.safaribooksonline.com/book/programming/c/1565923065/iidot-simple-programming/pcp3-chp-12"><cite>Practical C Programming (3rd Edition)</cite> Chapter 12</a></li>
        <li><a href="http://proquest.safaribooksonline.com/book/programming/c/1565923065/13dot-simple-pointers/pcp3-chp-17-sect-1"><cite>Practical C Programming (3rd Edition)</cite> Section 13.6</a></li>
        <li><a href="http://proquest.safaribooksonline.com/book/programming/c/1565923065/17dot-advanced-pointers/id472476"><cite>Practical C Programming (3rd Edition)</cite> Sections 17.1 - 17.2</a></li>
      </ul>
    </article>

    <article>
      <h2>Starter Code</h2>

      <p>
        Please download the starter code from above. It should contains a single file
        called <code>animal.c</code>.
      </p>
    </article>

    <article>
      <h2>Background</h2>

      <p><code>Animal</code> is a computer game in which the program tries to
        guess what animal the player is thinking of. A dialog might proceed as
        follows:</p>

      <pre v-highlightjs><code class="plain">Think of an animal. I will try to guess what it is.
Please answer my questions with yes or no.
Is it furry?
<span style="font-weight: bold">no</span>
Does it have tusks?
<span style="font-weight: bold">yes</span>
Does it have big ears?
<span style="font-weight: bold">no</span>
Is it a rhinoceros?</code></pre>

      <p>If the program's guess is correct, it will print a suitably celebratory
        message:</p>

      <pre v-highlightjs><code class="plain"><span style="font-weight: bold">yes</span>
I got it right!</code></pre>

      <p>If it guessed wrong, it asks for a question that will differentiate
        between its guess and the correct answer:</p>

      <pre v-highlightjs><code class="plain"><span style="font-weight: bold">no</span>
I give up. What is it? <span style="font-weight: bold">a wild boar</span>
Provide a question whose answer is yes
for a rhinoceros and no for a wild boar.
<span style="font-weight: bold">Does it have bad breath?</span></code></pre>

      <p>The program then updates its list of questions and answers
        appropriately.</p>

      <p>The <code>animal</code> program internally maintains a <dfn>tree</dfn>
        of questions and answers. A sample tree, from which the above dialog would
        be produced, is the following.</p>

      <figure>
        <img src="./images/animal-sample-tree.gif" class="img-responsive center-block" width="600">
        <figcaption>Figure 1. Animal Sample Tree</figcaption>
      </figure>
    </article>

    <article>
      <h2>Problem</h2>

      <p>A program framework for playing <code>animal</code> is given in <code>animal.c</code>.  Complete the
        framework. You may write extra functions beyond those suggested, but you
        may not change the main program.</p>

      <p>The animal tree is represented in the program as an array of pointers to
        strings (the questions and answers) using <code>typedef</code>. The 0th
        element is the root of the tree; the tree element corresponding to a "yes"
        answer for element <code>k</code> is element <code>2k+1</code>, and the
        tree element corresponding to a "no" answer for element <code>k</code> is
        element <code>2k+2</code>. An array for the above tree is</p>

      <figure>
        <img src="./images/animal-sample-tree-array.gif" class="img-responsive center-block" width="600">
        <figcaption>Figure 2. Animal Sample Tree Array</figcaption>
      </figure>
    </article>

    <article>
      <h2>Miscellaneous Requirements</h2>

      <p>Don't change any of the code that is already supplied; merely add code
        for the eleven incomplete functions, plus whatever auxiliary functions they
        call. The code is more complicated than one might expect. It is set up to
        simplify the use of a different data structure to store the questions and
        answers; you'll do this in the next programming assignment.</p>

      <p>You may assume that a question or answer is at most 80 characters long,
        and that the program has at most 32 questions in its repertoire. (Thus the
        array pictured above should only contain 32 pointers.) Your code for
        <code>InitTree</code> will call <code>malloc</code> or <code>calloc</code>;
        the constants <code>MAXSTRLEN</code> and <code>MAXNUMQS</code> are defined
        in the program for use in this call.</p>

      <p>Test your functions in isolation. You may wish to test your subprograms
        as you are building them, for instance, by first testing
        <code>InitTree</code> and <code>PrintTree</code>, then
        <code>Question</code>, <code>Answer</code>, and <code>GetNewInfo</code>,
        and so on. Bring evidence of this <dfn>incremental development</dfn> to the
        Self-Paced Center when you have this assignment graded.</p>

      <p>Test your complete program on guesses that exercise "yes" branches as
        well as "no" branches, and on games that result in successful guesses as
        well as unsuccessful guesses.</p>

      <p>You may initialize the tree any way you want, either with input from the
        user or with assignment statements. Programming assignment 4 will require
        that you initialize the tree from a file, so you might want to explore file
        operations like <code>fopen</code> ahead of time.</p>
    </article>

    <article>
      <h2>Helpful Hints</h2>

      <p>Dealing with pointers in C always causes beginners trouble.  A good
        strategy is to make diagrams of the pointer arrangements you are trying to
        produce and to refer to those diagrams when you run into difficulties.
        (Bring them to the Self-Paced Center if you need help from the tutoring
        staff!) Common mistakes include</p>

      <ul>
        <li>forgetting to place a null character at the end of a string,</li>
        <li>using a pointer before you have set it to point to anything,</li>
        <li>forgetting to allocate some memory to store an item,</li>
        <li>returning the address of a local variable in a function, and then having it deallocated out from under you</li>
        <li>confusing a pointer with the thing pointed to.</li>
      </ul>

      <p>Also, resist the impulse to combine the use of <code>*</code> and
        <code>++</code> or <code>––</code>.  For that matter, resist
        the impulse to use <code>++</code> or <code>--</code> <em>anywhere</em>
        except for on a line by themselves (e.g. <code>k++</code>).</p>
    </article>

    <article>
      <h2>Checklist</h2>
      <p>A satisfactory grade on this assignment requires the following:</p>
      <ul>
        <li>Correct behavior in guessing and adding nodes to the animal tree</li>
        <li>Sufficient test games</li>
        <li>Good style and layout</li>
        <li>Reasonable names for functions, variables, and parameters</li>
        <li>No function more than 24 lines long</li>
        <li>Appropriate use of auxiliary functions</li>
        <li>Evidence that subprograms were tested in isolation</li>
        <li>Use of <code>malloc</code> or <code>calloc</code> to allocate memory for the tree</li>
        <li>No changes to main program other than to complete the specified functions</li>
      </ul>
    </article>
  </section>
</template>



// WEBPACK FOOTER //
// src/components/assignments/c/TheAnimalGame.vue